import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { UnderbookedValue } from "./UnderbookedValue";
import { STATUS } from "../theme/tokens";
import {
  fetchWeeklyReport,
  getReportXlsxUrl,
  PERSONNEL_CATEGORY_OPTIONS,
  type PersonnelCategory,
  type WeeklyReport,
  type WeeklyReportQuery,
} from "../api/client";
import { useReportFetch } from "../hooks/useReportFetch";
import { REPORT_ABSENCE_TYPE_LABEL, employeeOptionsFromRows } from "./reportMeta";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Понедельник последней *полностью закрытой* недели относительно `today`.
 *
 * Семантика (см. design.md D9): PMO всегда смотрит уже завершившуюся неделю,
 * не текущую. Поэтому:
 *  - сегодня пн → открываем пн-7-дней-назад;
 *  - сегодня ср → открываем пн-9-дней-назад (пн прошлой недели);
 *  - сегодня вс → открываем пн-13-дней-назад.
 */
export function getLastMondayBefore(today: Date = new Date()): Date {
  const dow = today.getDay(); // 0=sunday, 1=monday, ..., 6=saturday
  const dayInWeek = (dow + 6) % 7; // 0=Mon, ..., 6=Sun
  const offset = dayInWeek + 7; // всегда попадаем в прошлую неделю
  const d = new Date(today);
  d.setDate(d.getDate() - offset);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toIso(d: Date | null): string | null {
  if (!d) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

function statusLabel(code: string): string {
  switch (code) {
    case "draft":
      return "Черновик";
    case "submitted_to_line_manager":
      return "На ЛР";
    case "rework_requested_by_line_manager":
      return "Возврат ЛР";
    case "waiting_project_managers":
      return "На PM";
    case "rework_requested_by_project_manager":
      return "Возврат PM";
    case "approved":
      return "Согласован";
    case "absence_only":
      return "Покрыт отсутствием";
    case "rolled_back_by_admin":
      return "Откат админа";
    case "no_week":
      return "Не создан";
    case "timesheet_exempt":
      return "Не ведёт таймшит";
    default:
      return code;
  }
}

function statusColor(code: string): string | undefined {
  if (code === "approved" || code === "absence_only") return "green";
  if (code === "no_week") return "red";
  // Освобождённый — не проблема, а исключение из правила: приглушённо, не красным.
  if (code === "timesheet_exempt") return "dimmed";
  if (code.startsWith("rework_requested")) return "orange";
  if (code.startsWith("submitted") || code.startsWith("waiting")) return "blue";
  return undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface WeeklySummaryViewProps {
  /** Drill-down: задаёт начальную неделю (вместо getLastMondayBefore()). */
  initialWeekStart?: Date | null;
  /** Drill-down: задаёт начального сотрудника-фильтр. */
  initialEmployeeId?: string | null;
}

export function WeeklySummaryView({
  initialWeekStart,
  initialEmployeeId = null,
}: WeeklySummaryViewProps = {}) {
  const defaultWeekStart = useMemo(() => getLastMondayBefore(), []);
  const [weekStart, setWeekStart] = useState<Date | null>(initialWeekStart ?? defaultWeekStart);
  const [employeeId, setEmployeeId] = useState<string | null>(initialEmployeeId);
  const [projectId, setProjectId] = useState<string | null>(null);
  // Категория персонала: отделяет тех, кто зарабатывает на проектах, от тех,
  // кто обеспечивает работу компании. Пусто = как раньше.
  const [category, setCategory] = useState<string | null>(null);

  const query: WeeklyReportQuery | null = useMemo(() => {
    const iso = toIso(weekStart);
    if (!iso) return null;
    return {
      weekStart: iso,
      ...(employeeId ? { employeeId: Number(employeeId) } : {}),
      ...(projectId ? { projectId: Number(projectId) } : {}),
      ...(category ? { category: category as PersonnelCategory } : {}),
    };
  }, [weekStart, employeeId, projectId, category]);

  const { report, loading, error } = useReportFetch(query, fetchWeeklyReport);

  // Опции для дропдауна сотрудников — берутся из текущего неделевого ответа
  // без фильтра (т.е. из последнего успешного report.rows). При выбранном
  // фильтре dropdown всё равно остаётся с выбранным значением.
  const employeeOptions = useMemo(
    () => (report ? employeeOptionsFromRows(report.rows) : []),
    [report],
  );
  // Опции проектов — из projects текущего ответа (код — имя). При выбранном
  // проекте список сужается до него и соведённых проектов; Clear вернёт полный.
  const projectOptions = useMemo(
    () =>
      report
        ? report.projects.map((p) => ({ value: String(p.id), label: `${p.code} — ${p.name}` }))
        : [],
    [report],
  );

  return (
    <Stack gap="md">
      <Title order={3}>Недельный свод</Title>
      <Text size="xs" c="dimmed">
        Отображается ваша зона ответственности за неделю. Максимум 100 сотрудников на страницу.
      </Text>
      <Group align="flex-end" gap="sm">
        <DatePickerInput
          label="Неделя (пн)"
          value={weekStart}
          onChange={(v) => {
            if (!v) {
              setWeekStart(null);
              return;
            }
            // v может прийти как Date | string | null — приводим к Date:
            const d = v instanceof Date ? v : new Date(v);
            if (d.getDay() !== 1) return; // защита от не-понедельника
            setWeekStart(d);
          }}
          valueFormat="DD.MM.YYYY"
          excludeDate={(d) => {
            const date = d instanceof Date ? d : new Date(d);
            return date.getDay() !== 1;
          }}
          w={180}
        />
        <Select
          label="Сотрудник"
          placeholder="Все"
          clearable
          searchable
          data={employeeOptions}
          value={employeeId}
          onChange={setEmployeeId}
          w={260}
          nothingFoundMessage="Нет совпадений"
        />
        <Select
          label="Проект"
          placeholder="Все"
          clearable
          searchable
          data={projectOptions}
          value={projectId}
          onChange={setProjectId}
          w={260}
          nothingFoundMessage="Нет совпадений"
        />
        <Select
          label="Категория персонала"
          placeholder="Все"
          clearable
          data={PERSONNEL_CATEGORY_OPTIONS}
          value={category}
          onChange={(value) => {
            setCategory(value);
            // Сбрасываем сотрудника: он мог не подходить под новую категорию,
            // и тогда таблица пустела, а имя продолжало висеть в фильтре —
            // без единого объяснения, почему строк нет.
            setEmployeeId(null);
          }}
          w={220}
        />
        <Button
          component="a"
          href={
            query
              ? getReportXlsxUrl({
                  kind: "weekly",
                  weekStart: query.weekStart,
                  employeeId: query.employeeId,
                  projectId: query.projectId,
                  // Файл обязан совпадать с экраном: без категории выгрузка
                  // отдавала другой набор строк под тем же именем.
                  category: query.category,
                })
              : undefined
          }
          disabled={loading || !query}
          variant="default"
          aria-label="Экспорт в Excel"
        >
          Экспорт в Excel
        </Button>
      </Group>

      {loading && <Loader size="sm" />}
      {error && (
        <Alert color="red" title="Не удалось загрузить отчёт">
          {error}
        </Alert>
      )}
      {!loading && !error && report && <WeeklyReportTable report={report} />}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface WeeklyReportTableProps {
  report: WeeklyReport;
}

function WeeklyReportTable({ report }: WeeklyReportTableProps) {
  if (report.rows.length === 0) {
    return <Text c="dimmed">На выбранной неделе никто не списывал часы и не отсутствовал.</Text>;
  }

  const { projects, absence_types: absenceTypes, rows, totals } = report;

  return (
    <ScrollArea>
      <Table withTableBorder highlightOnHover striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Сотрудник</Table.Th>
            <Table.Th>Статус</Table.Th>
            {projects.map((p) => (
              <Table.Th key={p.id} ta="right" title={p.name}>
                {p.code}
              </Table.Th>
            ))}
            {absenceTypes.map((t) => (
              <Table.Th key={t} ta="right">
                {REPORT_ABSENCE_TYPE_LABEL[t] ?? t}
              </Table.Th>
            ))}
            <Table.Th ta="right">Нераспределено</Table.Th>
            <Table.Th ta="right">Итого</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const projectHoursMap = new Map<number, string>();
            for (const ph of row.project_hours) {
              projectHoursMap.set(ph.project_id, ph.hours);
            }
            return (
              <Table.Tr key={row.employee.id}>
                <Table.Td>{row.employee.full_name}</Table.Td>
                <Table.Td>
                  <StatusPill code={row.timesheet_status} />
                </Table.Td>
                {projects.map((p) => (
                  <Table.Td key={p.id} ta="right">
                    {projectHoursMap.get(p.id) ?? "—"}
                  </Table.Td>
                ))}
                {absenceTypes.map((t) => (
                  <Table.Td key={t} ta="right">
                    {row.absence_hours_by_type[t] ?? "0.00"}
                  </Table.Td>
                ))}
                <Table.Td ta="right">
                  <UnderbookedValue
                    value={row.unallocated_hours}
                    danger={Number(row.unallocated_hours) > 0}
                  />
                </Table.Td>
                <Table.Td ta="right">{row.total_hours}</Table.Td>
              </Table.Tr>
            );
          })}
          {/* Итого */}
          <Table.Tr>
            <Table.Td fw={700}>Итого</Table.Td>
            <Table.Td></Table.Td>
            {projects.map((p) => {
              const cell = totals.project_hours.find((x) => x.project_id === p.id);
              return (
                <Table.Td key={p.id} ta="right" fw={700}>
                  {cell ? cell.hours : "0.00"}
                </Table.Td>
              );
            })}
            {absenceTypes.map((t) => (
              <Table.Td key={t} ta="right" fw={700}>
                {totals.absence_hours_by_type[t] ?? "0.00"}
              </Table.Td>
            ))}
            <Table.Td ta="right" fw={700}>
              <UnderbookedValue value={totals.unallocated} danger={Number(totals.unallocated) > 0} bold />
            </Table.Td>
            <Table.Td ta="right" fw={700}>
              {totals.total}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}

function StatusPill({ code }: { code: string }) {
  const { colorScheme } = useMantineColorScheme();
  // Ревью 21.07: «Не создан» (no_week) красным «кричал» на тёмном — даём мягкий
  // красный из STATUS-токена. Остальные статусы (зелёный/синий/оранжевый) — ок.
  if (code === "no_week") {
    const tok = STATUS[colorScheme === "dark" ? "dark" : "light"].rejected;
    return (
      <Text size="sm" span style={{ color: tok.fg }}>
        {statusLabel(code)}
      </Text>
    );
  }
  return (
    <Text size="sm" c={statusColor(code)} span>
      {statusLabel(code)}
    </Text>
  );
}
